import type {
  Action,
  ValidationError,
  ValidationResult,
} from "../../types/actions";
import type { Project } from "../../types/project";
import type { Clip, Effect, Track } from "../../types/timeline";
import type {
  MutableClip,
  MutableTimeline,
  MutableTrack,
} from "../../utils/immutable-updates";
import { createInverseAction, registerActionHandler } from "../registry";
import type { ActionHandler } from "../registry";
import { findClip, patchClip } from "./clip-helpers";

/**
 * Audio sits on the clip beside the video effects, so every action here is
 * addressed by clipId and every one of them opens with the same three checks:
 * the id is a string, the clip exists, and its track is not locked. The legacy
 * validator ran those once, before its switch. A handler's validate replaces
 * that whole path rather than being called after it, so they are restated here
 * instead of inherited, and they keep their early returns: a missing or unknown
 * clip reports one error and never reaches the per-type checks.
 *
 * audio/restoreEffect is the odd one out. It is minted only as the inverse of a
 * removal, never by a caller, so it is absent from the AudioAction union and had
 * no case in either the validator's or the inverse generator's switch. Falling
 * off the end of that switch returned undefined, and an entry with no inverse is
 * one undo refuses to consume, so anything that routed a restore through
 * execute() would wedge the undo stack rather than merely fail to undo. Nothing
 * dispatches one today, and it gets an invert here so that the first caller to
 * try does not have to find that out.
 */

function result(errors: ValidationError[]): ValidationResult {
  return { valid: errors.length === 0, errors };
}

function findTrackOfClip(project: Project, trackId: string): Track | null {
  return project.timeline.tracks.find((t) => t.id === trackId) ?? null;
}

/**
 * An action naming a clip that is not on the timeline is a silent no-op here
 * rather than a throw, which is what the legacy apply did for all eight of
 * these types. The executor turns a throw into a failed action, so tightening
 * this would report an error for an inverse whose clip was removed under it.
 */
function updateClip(
  project: Project,
  clipId: string,
  update: (clip: MutableClip) => MutableClip,
): void {
  const timeline = project.timeline as MutableTimeline;
  timeline.tracks = timeline.tracks.map((track: MutableTrack) => ({
    ...track,
    clips: track.clips.map((clip: MutableClip) =>
      clip.id === clipId ? update(clip) : clip,
    ),
  }));
}

type ClipCheck =
  | { readonly ok: false; readonly errors: ValidationError[] }
  | {
      readonly ok: true;
      readonly errors: ValidationError[];
      readonly clip: Clip;
    };

function checkClipTarget(action: Action, project: Project): ClipCheck {
  const clipId = (action.params as { clipId?: unknown }).clipId;

  if (!clipId || typeof clipId !== "string") {
    return {
      ok: false,
      errors: [
        {
          code: "INVALID_PARAMS",
          message: "Clip ID is required and must be a string",
          path: "params.clipId",
        },
      ],
    };
  }

  const clip = findClip(project, clipId);
  if (!clip) {
    return {
      ok: false,
      errors: [
        {
          code: "CLIP_NOT_FOUND",
          message: `Clip with ID ${clipId} not found`,
          path: "params.clipId",
        },
      ],
    };
  }

  const errors: ValidationError[] = [];
  if (findTrackOfClip(project, clip.trackId)?.locked) {
    errors.push({
      code: "TRACK_LOCKED",
      message: `Track containing clip ${clipId} is locked`,
      path: "params.clipId",
    });
  }

  return { ok: true, errors, clip };
}

type ExtraChecks = (
  params: Record<string, unknown>,
  clip: Clip,
) => ValidationError[];

function clipValidate(extra?: ExtraChecks) {
  return (action: Action, project: Project): ValidationResult => {
    const check = checkClipTarget(action, project);
    if (!check.ok) return result(check.errors);

    const errors = [...check.errors];
    if (extra) {
      errors.push(
        ...extra(action.params as Record<string, unknown>, check.clip),
      );
    }
    return result(errors);
  };
}

function clipInvert(build: (action: Action, clip: Clip) => Action | null) {
  return (action: Action, projectBefore: Project): Action | null => {
    const clipId = (action.params as { clipId: string }).clipId;
    const clip = findClip(projectBefore, clipId);
    if (!clip) return null;
    return build(action, clip);
  };
}

/**
 * The enabled check that follows this one in audio/toggleEffect runs whether or
 * not the effect id resolved, so a call that gets both wrong reports both.
 */
function checkEffectId(
  params: Record<string, unknown>,
  clip: Clip,
): ValidationError[] {
  const effectId = params.effectId;

  if (!effectId || typeof effectId !== "string") {
    return [
      {
        code: "INVALID_PARAMS",
        message: "Effect ID is required and must be a string",
        path: "params.effectId",
      },
    ];
  }

  return (clip.audioEffects ?? []).some((e) => e.id === effectId)
    ? []
    : [
        {
          code: "EFFECT_NOT_FOUND",
          message: `Audio effect with ID ${effectId} not found on clip`,
          path: "params.effectId",
        },
      ];
}

const audioSetVolume: ActionHandler = {
  type: "audio/setVolume",
  validate: clipValidate((params) => {
    const volume = params.volume;
    return typeof volume !== "number" || volume < 0 || volume > 4
      ? [
          {
            code: "INVALID_PARAMS",
            message: "Volume must be a number between 0 and 4",
            path: "params.volume",
          },
        ]
      : [];
  }),
  apply(action: Action, project: Project): void {
    const params = action.params as { clipId: string; volume: number };
    patchClip(project, params.clipId, { volume: params.volume });
  },
  invert: clipInvert((action, clip) =>
    createInverseAction(action, "audio/setVolume", {
      clipId: (action.params as { clipId: string }).clipId,
      volume: clip.volume,
    }),
  ),
};

const audioSetFade: ActionHandler = {
  type: "audio/setFade",
  validate: clipValidate((params) => {
    const errors: ValidationError[] = [];
    const fadeIn = params.fadeIn;
    const fadeOut = params.fadeOut;
    if (fadeIn !== undefined && (typeof fadeIn !== "number" || fadeIn < 0)) {
      errors.push({
        code: "INVALID_PARAMS",
        message: "Fade-in must be a non-negative number",
        path: "params.fadeIn",
      });
    }
    if (fadeOut !== undefined && (typeof fadeOut !== "number" || fadeOut < 0)) {
      errors.push({
        code: "INVALID_PARAMS",
        message: "Fade-out must be a non-negative number",
        path: "params.fadeOut",
      });
    }
    return errors;
  }),
  apply(action: Action, project: Project): void {
    const params = action.params as {
      clipId: string;
      fadeIn?: number;
      fadeOut?: number;
    };
    updateClip(project, params.clipId, (clip) => ({
      ...clip,
      fade: {
        fadeIn:
          params.fadeIn !== undefined ? params.fadeIn : clip.fade?.fadeIn || 0,
        fadeOut:
          params.fadeOut !== undefined
            ? params.fadeOut
            : clip.fade?.fadeOut || 0,
      },
    }));
  },
  // Both ends go back even when the action only moved one of them, so an undo
  // of a fade-in edit cannot leave a fade-out the same action never touched.
  invert: clipInvert((action, clip) =>
    createInverseAction(action, "audio/setFade", {
      clipId: (action.params as { clipId: string }).clipId,
      fadeIn: clip.fade?.fadeIn ?? 0,
      fadeOut: clip.fade?.fadeOut ?? 0,
    }),
  ),
};

const audioAddAutomation: ActionHandler = {
  type: "audio/addAutomation",
  validate: clipValidate((params) =>
    Array.isArray(params.points)
      ? []
      : [
          {
            code: "INVALID_PARAMS",
            message: "Automation points must be an array",
            path: "params.points",
          },
        ],
  ),
  apply(action: Action, project: Project): void {
    const params = action.params as {
      clipId: string;
      points: Array<{ time: number; value: number }>;
    };
    updateClip(project, params.clipId, (clip) => ({
      ...clip,
      automation: {
        ...clip.automation,
        volume: params.points,
      },
    }));
  },
  invert: clipInvert((action, clip) =>
    createInverseAction(action, "audio/addAutomation", {
      clipId: (action.params as { clipId: string }).clipId,
      points: clip.automation?.volume ?? [],
    }),
  ),
};

const audioAddEffect: ActionHandler = {
  type: "audio/addEffect",
  validate: clipValidate((params) => {
    const effect = params.effect as { id?: unknown } | undefined;
    return effect && typeof effect.id === "string"
      ? []
      : [
          {
            code: "INVALID_PARAMS",
            message: "A valid effect object with an id is required",
            path: "params.effect",
          },
        ];
  }),
  apply(action: Action, project: Project): void {
    const params = action.params as { clipId: string; effect: Effect };
    updateClip(project, params.clipId, (clip) => ({
      ...clip,
      audioEffects: [...(clip.audioEffects ?? []), params.effect],
    }));
  },
  // The caller mints the effect id, so the inverse can name it outright and has
  // no need for the __LAST_ADDED__ marker the subtitle and marker adds use.
  invert: clipInvert((action) => {
    const effect = (action.params as { effect: Effect }).effect;
    return createInverseAction(action, "audio/removeEffect", {
      clipId: (action.params as { clipId: string }).clipId,
      effectId: effect.id,
    });
  }),
};

const audioRestoreEffect: ActionHandler = {
  type: "audio/restoreEffect",
  validate: clipValidate(),
  apply(action: Action, project: Project): void {
    const params = action.params as {
      clipId: string;
      effect: Effect;
      index: number;
    };
    updateClip(project, params.clipId, (clip) => {
      const audioEffects = [...(clip.audioEffects ?? [])];
      audioEffects.splice(params.index, 0, params.effect);
      return { ...clip, audioEffects };
    });
  },
  invert: clipInvert((action) => {
    const effect = (action.params as { effect?: Effect }).effect;
    // validate() has no case for this type, so a hand-rolled restore can reach
    // here with no effect at all. The legacy switch returned undefined for
    // every restore, so declining this one loses nothing.
    if (!effect || typeof effect.id !== "string") return null;
    return createInverseAction(action, "audio/removeEffect", {
      clipId: (action.params as { clipId: string }).clipId,
      effectId: effect.id,
    });
  }),
};

const audioRemoveEffect: ActionHandler = {
  type: "audio/removeEffect",
  validate: clipValidate(checkEffectId),
  apply(action: Action, project: Project): void {
    const params = action.params as { clipId: string; effectId: string };
    updateClip(project, params.clipId, (clip) => ({
      ...clip,
      audioEffects: (clip.audioEffects ?? []).filter(
        (e: Effect) => e.id !== params.effectId,
      ),
    }));
  },
  invert: clipInvert((action, clip) => {
    const params = action.params as { clipId: string; effectId: string };
    const index = clip.audioEffects.findIndex((e) => e.id === params.effectId);
    if (index === -1) return null;
    const effect = clip.audioEffects[index];

    // The params object is copied so the stored inverse cannot alias state that
    // a later edit replaces before undo ever reaches it.
    return createInverseAction(action, "audio/restoreEffect", {
      clipId: params.clipId,
      effect: { ...effect, params: { ...effect.params } },
      index,
    });
  }),
};

const audioUpdateEffect: ActionHandler = {
  type: "audio/updateEffect",
  validate: clipValidate(checkEffectId),
  apply(action: Action, project: Project): void {
    const params = action.params as {
      clipId: string;
      effectId: string;
      params: Record<string, unknown>;
    };
    updateClip(project, params.clipId, (clip) => ({
      ...clip,
      audioEffects: (clip.audioEffects ?? []).map((e: Effect) =>
        e.id === params.effectId
          ? { ...e, params: { ...e.params, ...params.params } }
          : e,
      ),
    }));
  },
  invert: clipInvert((action, clip) => {
    const params = action.params as { clipId: string; effectId: string };
    const effect = clip.audioEffects.find((e) => e.id === params.effectId);
    if (!effect) return null;

    // apply() merges rather than replaces, so restoring the whole prior params
    // object is what puts back a key the action overwrote.
    return createInverseAction(action, "audio/updateEffect", {
      clipId: params.clipId,
      effectId: params.effectId,
      params: { ...effect.params },
    });
  }),
};

const audioToggleEffect: ActionHandler = {
  type: "audio/toggleEffect",
  validate: clipValidate((params, clip) => {
    const errors = checkEffectId(params, clip);
    if (typeof params.enabled !== "boolean") {
      errors.push({
        code: "INVALID_PARAMS",
        message: "Enabled parameter must be a boolean",
        path: "params.enabled",
      });
    }
    return errors;
  }),
  apply(action: Action, project: Project): void {
    const params = action.params as {
      clipId: string;
      effectId: string;
      enabled: boolean;
    };
    updateClip(project, params.clipId, (clip) => ({
      ...clip,
      audioEffects: (clip.audioEffects ?? []).map((e: Effect) =>
        e.id === params.effectId ? { ...e, enabled: params.enabled } : e,
      ),
    }));
  },
  invert: clipInvert((action, clip) => {
    const params = action.params as { clipId: string; effectId: string };
    const effect = clip.audioEffects.find((e) => e.id === params.effectId);
    if (!effect) return null;

    return createInverseAction(action, "audio/toggleEffect", {
      clipId: params.clipId,
      effectId: params.effectId,
      enabled: effect.enabled,
    });
  }),
};

for (const handler of [
  audioSetVolume,
  audioSetFade,
  audioAddAutomation,
  audioAddEffect,
  audioRestoreEffect,
  audioRemoveEffect,
  audioUpdateEffect,
  audioToggleEffect,
]) {
  registerActionHandler(handler);
}
